"use client";

import { Flower2, Leaf, Sun, Moon, Sparkles, Coffee, Tag, Users, Calendar, Bath, BookOpen, Heart, Star, Gift, Wind } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheBenewealthAdvantage() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const categories = [{
    title: "Strategic Architecture",
    benefits: [{
      icon: Sparkles,
      name: "Credit Blueprinting",
      description: "Precision engineering of your personal and business credit profiles for maximum leverage."
    }, {
      icon: Wind,
      name: "Funding Readiness Audit",
      description: "Comprehensive analysis to ensure your business meets elite lender requirements."
    }, {
      icon: Star,
      name: "Priority Capital Access",
      description: "Direct pathways to high-limit credit lines and strategic funding solutions."
    }, {
      icon: Tag,
      name: "Exclusive Vendor Lists",
      description: "Curated access to tier-one vendors that report to major business credit bureaus."
    }]
  }, {
    title: "Elite Advisory",
    benefits: [{
      icon: Users,
      name: "Private Wealth Mentoring",
      description: "Direct access to senior strategists for high-level financial decision making."
    }, {
      icon: BookOpen,
      name: "Wealth Strategy Library",
      description: "Proprietary resources on tax mitigation, asset protection, and capital growth."
    }, {
      icon: Heart,
      name: "Quarterly Portfolio Reviews",
      description: "Deep-dive sessions to recalibrate your financial architecture for changing markets."
    }, {
      icon: Moon,
      name: "Discreet Concierge Service",
      description: "White-glove handling of all documentation and lender communications."
    }, {
      icon: Coffee,
      name: "Executive Lounge Access",
      description: "Invitations to our private networking events for high-net-worth entrepreneurs."
    }]
  }, {
    title: "Capital Solutions",
    benefits: [{
      icon: Gift,
      name: "Partner Referral Program",
      description: "Exclusive incentives for introducing qualified peers to the Benewealth network."
    }, {
      icon: Calendar,
      name: "Early Opportunity Alerts",
      description: "First-look access to emerging investment vehicles and funding programs."
    }, {
      icon: Leaf,
      name: "Sustainable Growth Planning",
      description: "Long-term strategies designed to build generational wealth, not just quick capital."
    }, {
      icon: Bath,
      name: "Risk Mitigation Suite",
      description: "Advanced tools to protect your credit and assets from market volatility."
    }, {
      icon: Sun,
      name: "Global Funding Network",
      description: "Leverage our international relationships for diverse capital requirements."
    }]
  }];
  return <section data-section-id="158" id="the-benewealth-advantage" className="bg-background text-foreground py-[140px] px-6 lg:px-12 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
      backgroundSize: '50px 50px'
    }} />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {}
        <div ref={headerRef} className={`text-center mb-20 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-primary mb-6 font-bold">
            § 02 — The Advantage
          </p>
          <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-[52px] leading-tight text-foreground">
            Architecting <em className="italic text-primary not-italic">Capital.</em>
          </h2>
          <div className="w-12 h-px bg-primary mx-auto mt-8" />
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div ref={imageRef} className={`lg:col-span-4 transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24 overflow-hidden rounded-md shadow-xl border border-border">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/luxury_office_blueprint.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/luxury_office_blueprint.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/luxury_office_blueprint.png-wvc-sizes" alt="High-end executive office with architectural blueprints and luxury financial documents" loading="lazy" decoding="async" className="w-full h-[400px] lg:h-[720px] object-cover grayscale contrast-125" />
            </div>
          </div>

          {}
          <div ref={contentRef} className={`lg:col-span-8 transition-all duration-1000 delay-150 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-14">
              {categories.map((category, catIndex) => <div data-index={catIndex} key={catIndex}>
                  {catIndex > 0 && <div className="w-full h-px bg-border mb-14" aria-hidden="true" />}

                  <h3 className="font-serif font-default text-2xl text-foreground mb-8 italic">
                    {category.title}
                  </h3>

                  <ul className="space-y-6">
                    {category.benefits.map((benefit, i) => {
                  const Icon = benefit.icon;
                  return <li data-index={i} key={i} className="group flex items-start gap-5 cursor-default">
                          <div className="shrink-0 mt-1">
                            <Icon className="w-5 h-5 text-primary transition-all duration-500 ease-out group-hover:scale-110" strokeWidth={1} aria-hidden="true" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-serif font-default text-lg text-foreground transition-colors duration-500 group-hover:text-primary">
                              {benefit.name}
                            </h4>
                            <p className="font-sans text-sm text-muted-foreground mt-1 leading-relaxed">
                              {benefit.description}
                            </p>
                          </div>
                        </li>;
                })}
                  </ul>
                </div>)}
            </div>

            {}
            <div className="mt-16 pt-10 border-t border-border">
              <p className="font-serif font-default text-xl italic text-muted-foreground leading-relaxed">
                "true wealth is not merely the accumulation of assets, but the precision of the architecture that supports them."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}